import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Route, Link } from "react-router-dom";
import { Layout, Menu, Breadcrumb, Icon, Card } from 'antd';
import '../../assets/css/App.css';
import routes from '../../model/router';
import Header1 from '../public';
import BicombImg from "../../assets/images/clarityICI.jpg";

// ===============================
// 1) CLARITY-ICI Matrix (All) data object (from attached image)
//    - Preserves coordinates: rowIndex, colIndex
//    - Preserves values as shown (Months)
// ===============================
const CLARITY_ICI_ALL = {
  title: " ",
  xAxisLabel: "Step 2: Median survival outcomes",
  yAxisLabel: "Step 1: Identify your interested variables and the target events",
  xLabels: ["ECOG ≥1", "ECOG <1", "Stage IV", "Stage I~III"],
  rows: [
    { yLabel: "Age >65 OS", values: [null, null, 14.7, 21.6] },
    { yLabel: "Age >65 PFS", values: [4.0, 5.3, null, null] },
    { yLabel: "Age ≤65 OS", values: [11.0, 14.9, 15.9, 20.6] },
    { yLabel: "Age ≤65 PFS", values: [3.3, 6.0, null, null] },
    { yLabel: "ECOG ≥1 OS", values: [null, null, 12.0, 17.0] },
    { yLabel: "IO_Type Mono PFS", values: [3.0, 5.4, null, null] },
    { yLabel: "Line 1L OS", values: [null, null, 14.6, 20.1] },
    { yLabel: "Line 1L PFS", values: [6.3, 9.6, null, null] },
    { yLabel: "Line ≥2L OS", values: [7.4, 14.0, 13.5, 20.8] },
    { yLabel: "Line ≥2L PFS", values: [2.7, 4.0, null, null] },
    { yLabel: "P_Treat No OS", values: [null, null, 14.6, 20.1] },
    { yLabel: "P_Treat No PFS", values: [6.4, 9.3, null, null] },
    { yLabel: "P_Treat Yes OS", values: [7.4, 14.0, 13.5, 20.8] },
    { yLabel: "P_Treat Yes PFS", values: [2.7, 4.0, null, null] },
    { yLabel: "Patho LUAD OS", values: [12.2, 19.0, 17.6, 22.2] },
    { yLabel: "Patho LUAD PFS", values: [4.2, 8.1, null, null] },
    { yLabel: "Patho LUSC OS", values: [9.2, 16.1, null, null] },
    { yLabel: "Patho Other OS", values: [null, null, 15.0, 18.8] },
    { yLabel: "Real_World Yes OS", values: [9.0, 14.7, 14.3, 21.8] },
    { yLabel: "Real_World Yes PFS", values: [3.3, 6.4, null, null] },
    { yLabel: "Sex Female OS", values: [11.1, 13.6, 16.1, 20.6] },
    { yLabel: "Sex Male OS", values: [11.1, 15.9, 14.3, 21.2] },
    { yLabel: "Sex Male PFS", values: [4.1, 7.1, 5.8, 10.0] },
    { yLabel: "Smoking No OS", values: [14.8, 14.0, 18.7, 23.1] },
    { yLabel: "Smoking Yes OS", values: [10.1, 16.3, 15.0, 20.5] },
    { yLabel: "Smoking Yes PFS", values: [4.1, 7.6, null, null] },
    { yLabel: "Stage IV OS", values: [12.0, 14.5, null, null] },
    { yLabel: "Stage IV PFS", values: [4.1, 6.5, null, null] },
    { yLabel: "TMB High OS", values: [9.6, 14.6, 14.6, 19.8] },
    { yLabel: "TMB Low OS", values: [null, null, 13.4, 20.7] },
    { yLabel: "TMB Low PFS", values: [null, null, 3.1, 6.7] },
  ],
  valueMin: 2.7,
  valueMax: 23.1,
};

// ===============================
// 2) Color scale helper (blue -> white -> green)
// ===============================
const lerp = (a, b, t) => a + (b - a) * t;

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}
function rgbToHex({ r, g, b }) {
  const toHex = (x) => x.toString(16).padStart(2, "0");
  return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`;
}
function interpolateHex(c1, c2, t) {
  const a = hexToRgb(c1);
  const b = hexToRgb(c2);
  return rgbToHex({
    r: lerp(a.r, b.r, t),
    g: lerp(a.g, b.g, t),
    b: lerp(a.b, b.b, t),
  });
}
function valueToColor(v, vMin, vMax) {
  if (v === null || v === undefined) return "transparent";
  const t = Math.min(1, Math.max(0, (v - vMin) / (vMax - vMin)));
  const blue = "#08306b";
  const white = "#f7f7f7";
  const green = "#2ca25f";
  if (t <= 0.5) return interpolateHex(blue, white, t / 0.5);
  return interpolateHex(white, green, (t - 0.5) / 0.5);
}

// ===============================
// 3) Tooltip sentence builder
//    A = x-axis label text
//    B = y-axis label text
//    C = OS or PFS from y-axis label suffix
//    D = cell value (months)
// ===============================
function parseOutcomeFromY(yLabel) {
  const s = (yLabel || "").trim();
  if (s.endsWith(" OS")) return "OS";
  if (s.endsWith(" PFS")) return "PFS";
  // fallback: try to find " OS"/" PFS" anywhere near the end
  const m = s.match(/\b(OS|PFS)\b\s*$/);
  return m ? m[1] : "";
}
function removeOutcomeSuffix(yLabel) {
  return (yLabel || "").replace(/\s+(OS|PFS)\s*$/i, "").trim();
}
function buildHoverSentence({ xLabel, yLabel, value }) {
  const B = (xLabel || "").trim();
  const A = removeOutcomeSuffix(yLabel);
  const C = parseOutcomeFromY(yLabel);
  const D = value;
  return `Patient with the stage 1 variable of ${A} (level) and the stage 2 variable of ${B} (level), the corresponding median ${C} stratification is ${D} months.`;
}

// ===============================
// 4) Matrix renderer (left-aligned + smaller x-labels + cursor tooltip)
// ===============================
class ClarityMatrixGrid extends React.PureComponent {
  render() {
    const { data, hover, tooltip, onEnter, onMove, onLeave } = this.props;
    const { xLabels, rows, valueMin, valueMax, xAxisLabel, yAxisLabel } = data;

    // Layout constants
    const cellW = 88;
    const cellH = 30;
    const yLabelW = 220;

    const isHover = (ri, ci) => hover && hover.r === ri && hover.c === ci;

    const axisLabelStyle = (active) => ({
      fontWeight: active ? "bold" : "normal",
      color: active ? "#111" : "#666",
      opacity: active ? 1 : 0.85,
      transition: "all 120ms ease",
      userSelect: "none",
    });

    return (
      <div style={{ position: "relative", marginTop: 10 }}>
        {/* Tooltip near cursor */}
        {tooltip && tooltip.visible ? (
          <div
            style={{
              position: "fixed",
              left: tooltip.x + 12,
              top: tooltip.y + 12,
              maxWidth: 420,
              background: "rgba(17,17,17,0.92)",
              color: "#fff",
              padding: "8px 10px",
              borderRadius: 6,
              fontSize: 12,
              lineHeight: "16px",
              zIndex: 9999,
              pointerEvents: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
            }}
          >
            {tooltip.text}
          </div>
        ) : null}

        {/* Axis titles row (yAxisLabel aligned to the left edge of the color blocks) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${yLabelW}px repeat(${xLabels.length}, ${cellW}px)`,
            alignItems: "end",
            marginBottom: 0,
          }}
        >
          {/* y-axis label: italic, right-aligned to the boundary where color blocks start */}
          <div
            style={{
              fontSize: 12,
              color: "#666",
              fontWeight: "bold",
              fontStyle: "italic",
              textAlign: "right",
              lineHeight: "14px",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end", // 关键：右端严格贴齐色块左边界（yLabelW的右边界）
              paddingRight: 0,
              marginRight: 0,
              userSelect: "none",
            }}
          >
           {/* yAxisLabel */}
          </div>

          {/* x-axis label centered over the color-block area */}
          <div
            style={{
              gridColumn: `span ${xLabels.length}`,
              textAlign: "center",
              fontSize: 12,
              color: "#666",
              fontWeight: "bold",
              userSelect: "none",
            }}
          >
           {/* xAxisLabel */}
          </div>
        </div>

        {/* X-axis labels (smaller font) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${yLabelW}px repeat(${xLabels.length}, ${cellW}px)`,
            alignItems: "end",
            marginBottom: 6,
          }}
        >
          <div></div>
          {xLabels.map((xl, ci) => {
            const active = hover && hover.c === ci;
            return (
              <div
                key={xl}
                style={{
                  ...axisLabelStyle(active),
                  textAlign: "center",
                  transform: "rotate(-35deg)",
                  transformOrigin: "center",
                  height: 34,
                  lineHeight: "12px",
                  fontSize: 11, // (1) reduced x-axis label size
                }}
              >
                {xl}
              </div>
            );
          })}
        </div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${yLabelW}px repeat(${xLabels.length}, ${cellW}px)`,
            gridAutoRows: `${cellH}px`,
            gap: 2,
          }}
        >
          {rows.map((row, ri) => {
            const rowActive = hover && hover.r === ri;

            return (
              <React.Fragment key={row.yLabel}>
                {/* Y label */}
                <div
                  style={{
                    ...axisLabelStyle(rowActive),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: 10,
                    fontSize: 13,
                  }}
                >
                  {row.yLabel}
                </div>

                {/* Cells */}
                {row.values.map((v, ci) => {
                  const active = isHover(ri, ci);
                  const bg = valueToColor(v, valueMin, valueMax);
                  const isEmpty = v === null || v === undefined;

                  return (
                    <div
                      key={`${ri}-${ci}`}
                      onMouseEnter={(e) => {
                        if (isEmpty) return;
                        onEnter(e, ri, ci, {
                          xLabel: xLabels[ci],
                          yLabel: row.yLabel,
                          value: v,
                        });
                      }}
                      onMouseMove={(e) => {
                        if (isEmpty) return;
                        onMove(e);
                      }}
                      onMouseLeave={onLeave}
                      style={{
                        width: cellW,
                        height: cellH,
                        background: bg,
                        border: active ? "2px solid #111" : "1px solid #e6e6e6",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: active ? "bold" : "normal",
                        color: isEmpty ? "transparent" : "#111",
                        cursor: isEmpty ? "default" : "pointer",
                        filter: active ? "brightness(1.08)" : "none",
                        transition: "all 120ms ease",
                        userSelect: "none",
                      }}
                    >
                      {isEmpty ? "" : v}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>


      </div>
    );
  }
}

const { Header, Content, Footer, Sider } = Layout;
const SubMenu = Menu.SubMenu;
const { Meta } = Card;

const Ajax = require('axios');

class ClarityICI extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hoverCell: null, // { r, c }
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        text: "",
      },
    };
  }

  handleEnterCell = (e, r, c, meta) => {
    const text = buildHoverSentence(meta);
    this.setState({
      hoverCell: { r, c },
      tooltip: {
        visible: true,
        x: e.clientX,
        y: e.clientY,
        text,
      },
    });
  };

  handleMoveCell = (e) => {
    // lightweight cursor-follow
    const { tooltip } = this.state;
    if (!tooltip.visible) return;
    this.setState({
      tooltip: {
        ...tooltip,
        x: e.clientX,
        y: e.clientY,
      },
    });
  };

  handleLeaveCell = () => {
    this.setState({
      hoverCell: null,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        text: "",
      },
    });
  };

  render() {
    return (
      <div>
        <Header1></Header1>
        <Content style={{ padding: '0 50px' }}>
          <Layout style={{ background: '#fff' }}>
            <Sider width={200} style={{ background: '#fff' }}>
              <Menu
                mode="inline"
                defaultSelectedKeys={['2']}
                defaultOpenKeys={['sub1']}
                style={{ height: '100%' }}
                >
                <SubMenu key="sub1" title={<span><Icon type="user" />CLARITY-ICI</span>}>
                <Menu.Item key="1"><Link to="/clarityICI">Introduction</Link></Menu.Item>
                <Menu.Item key="2"><Link to="/clarityICIAll">All Population</Link></Menu.Item>
                <Menu.Item key="3"><Link to="/clarityICIWestern">Western</Link></Menu.Item>
                <Menu.Item key="4"><Link to="/clarityICIEastAsian">East Asian</Link></Menu.Item>
                <Menu.Item key="5"><Link to="/clarityICIIntroAnn">Animation</Link></Menu.Item>
                </SubMenu>
              </Menu>
            </Sider>

            <Content style={{ padding: '0 50px' }}>
              <div
                style={{
                  background: `url(${BicombImg}) no-repeat center center`,
                  height: 300,
                  width: "100%",
                  backgroundSize: "cover",
                }}
              >
                <div style={{ textAlign: "center", color: "#fff" }}>
                  <br></br><br></br><br></br>
                  <txt style={{ fontSize: 45, fontWeight: 'bold' }}>
                    Clinical Attribute–Based Risk Matrix for Prognostication in Immunotherapy-Treated NSCLC
                  </txt>
                  <br></br>
                </div>
              </div>

              <div>
                <br></br>
                <br></br>
                <txt style={{ fontSize: 23, fontWeight: 'bold',  paddingRight: 100 }}>
                  All Population CLARITY-ICI Matrix
                </txt>
                <br></br>

                <div style={{ fontSize: 20, paddingRight: 200 }}>
                  <txt>
                    <br></br>
                    
                    {/* (2) Left-aligned with the heading above: remove extra paddingLeft here */}
                    <div style={{ marginTop: 6 }}>
                      <ClarityMatrixGrid
                        data={CLARITY_ICI_ALL}
                        hover={this.state.hoverCell}
                        tooltip={this.state.tooltip}
                        onEnter={this.handleEnterCell}
                        onMove={this.handleMoveCell}
                        onLeave={this.handleLeaveCell}
                      />
                    </div>

                    <br></br><br></br>Please cite the following study if use the information in this website:
                    <br></br><br></br>
                    <txt style={{ fontStyle: 'italic' }}>
                     Clinical Attribute–Based Risk Matrix for Prognostication in Immunotherapy-Treated Non-Small-Cell Lung Cancer: An Individual-Patient Data Analysis of 17051 Patients.
                    </txt>
                    <br></br><br></br>

            
                  </txt>
                </div>
              </div>

            </Content>
          </Layout>
        </Content>
      </div>
    );
  }
}

export default ClarityICI;
